import React, { useMemo, useState, useEffect } from 'react';
import { FALLBACK_IMAGE } from '../../utils/imageUtils';

const buildProxyUrl = (fileId, sizeHint = 'w1200') => {
  if (!fileId) return '';
  return `/api/image-proxy?id=${encodeURIComponent(fileId)}&sz=${encodeURIComponent(sizeHint)}`;
};

// Extract Google Drive file id from various link formats
const extractDriveId = (url) => {
  if (!url) return null;
  try {
    if (typeof url !== 'string') return null;
    const raw = url.trim();
    if (raw.startsWith('/api/image-proxy')) return null;
    if (!raw.includes('drive.google.com') && !raw.includes('googleusercontent.com')) {
      const plainIdMatch = raw.match(/^([a-zA-Z0-9_-]{25,})$/);
      return plainIdMatch ? plainIdMatch[1] : null;
    }
    const byPath = raw.match(/\/file\/d\/([a-zA-Z0-9-_]+)/);
    if (byPath) return byPath[1];
    const byQuery = raw.match(/[?&]id=([a-zA-Z0-9-_]+)/);
    if (byQuery) return byQuery[1];
    const byLh = raw.match(/lh3\.googleusercontent\.com\/d\/([a-zA-Z0-9-_]+)/);
    if (byLh) return byLh[1];
    return null;
  } catch {
    return null;
  }
};

// Build a list of candidate URLs to try for an image
const buildCandidates = (src) => {
  if (!src) return [];
  const id = extractDriveId(src);
  if (!id) {
    return [src];
  }
  return [
    buildProxyUrl(id, 'w1200'),
    buildProxyUrl(id, 'w1600-h1600'),
    buildProxyUrl(id, 'w800'),
    buildProxyUrl(id, 'w2000-h2000'),
  ];
};

const SmartImage = ({ src, alt = '', style, className, width, height, onLoad, onError, imgStyle, ...rest }) => {
  const candidates = useMemo(() => buildCandidates(src), [src]);
  const [index, setIndex] = useState(0);
  const [currentSrc, setCurrentSrc] = useState(candidates[0] || '');

  useEffect(() => {
    setIndex(0);
    setCurrentSrc(candidates[0] || '');
  }, [candidates]);

  const handleError = (e) => {
    if (index < candidates.length - 1) {
      const next = index + 1;
      setIndex(next);
      setCurrentSrc(candidates[next]);
    } else {
      if (e && e.target) {
        e.target.onerror = null;
        e.target.src = FALLBACK_IMAGE;
      }
      if (onError) onError(e);
    }
  };

  const handleLoad = (e) => {
    if (onLoad) onLoad(e);
  };

  return (
    <img
      src={currentSrc}
      alt={alt}
      width={width}
      height={height}
      style={imgStyle || style}
      className={className}
      referrerPolicy="no-referrer"
      loading="lazy"
      onLoad={handleLoad}
      onError={handleError}
      {...rest}
    />
  );
};

export default SmartImage;


